import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SaveOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Divider,
  Form,
  InputNumber,
  message,
  Row,
  Space,
  Switch,
  Tag, Modal} from 'antd';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Component from '../../../../shared/components';
import { inhouseSyncMonitorService } from '../service/InhouseSyncMonitorService';
import { TestSyncResponse } from '../types/videoSyncMonitorType';
import './InhouseSyncMonitorTest.css';

const PRESET_OPTIONS = ['300p', '360p', '480p', '720p', '1080p'];

const InhouseSyncMonitorTest: React.FC = () => {
  const navigate = useNavigate();
  const [contentForm] = Form.useForm();
  const [trackerForm] = Form.useForm();

  const [testingContent, setTestingContent] = useState(false);
  const [testingTracker, setTestingTracker] = useState(false);
  const [testResult, setTestResult] = useState<TestSyncResponse | null>(null);
  const [selectedPresets, setSelectedPresets] = useState<string[]>([]);
  const [saveResultContent, setSaveResultContent] = useState(false);
  const [saveResultTracker, setSaveResultTracker] = useState(false);

  const handleTestByContent = async (values: { content_id: number }) => {
    setTestingContent(true);
    setTestResult(null);
    try {
      const result = await inhouseSyncMonitorService.testSync({
        content_id: values.content_id,
        expected_presets: selectedPresets.length > 0 ? selectedPresets : undefined,
        save_result: saveResultContent,
      });
      setTestResult(result);
      if (result.saved) {
        message.success('Test completed - results have been saved.');
      } else {
        message.success('Test completed');
      }
    } catch (error: any) {
      console.error('Failed to test:', error);
      Modal.error({ title: 'Error', content: error.response?.data?.error || 'Test failed.', okText: 'Confirm' });
    } finally {
      setTestingContent(false);
    }
  };

  const handleTestByTracker = async (values: { tracker_id: number }) => {
    setTestingTracker(true);
    setTestResult(null);
    try {
      const result = await inhouseSyncMonitorService.testSyncByTracker({
        tracker_id: values.tracker_id,
        save_result: saveResultTracker,
      });
      setTestResult(result);
      if (result.saved) {
        message.success('Test completed - results have been saved.');
      } else {
        message.success('Test completed');
      }
    } catch (error: any) {
      console.error('Failed to test:', error);
      Modal.error({ title: 'Error', content: error.response?.data?.error || 'Test failed.', okText: 'Confirm' });
    } finally {
      setTestingTracker(false);
    }
  };

  const handlePresetChange = (checkedValues: string[]) => {
    setSelectedPresets(checkedValues);
  };

  return (
    <Component.BasePageComponent>
      <div className="video-sync-test-container">
        {/* Header */}
        <div className="test-header">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/inhouse-sync-monitor')}>
            Back to List
          </Button>
        </div>

        <Row gutter={[16, 16]}>
          {/* Test by Content ID */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <SearchOutlined />
                  Test by Content ID
                </Space>
              }
              className="test-card"
            >
              <Form form={contentForm} layout="vertical" onFinish={handleTestByContent}>
                <Form.Item
                  name="content_id"
                  label="Content ID"
                  rules={[{ required: true, message: 'Please enter Content ID' }]}
                >
                  <InputNumber placeholder="12345" style={{ width: '100%' }} min={1} />
                </Form.Item>

                <Form.Item label="Expected Presets (optional)">
                  <Checkbox.Group
                    options={PRESET_OPTIONS}
                    value={selectedPresets}
                    onChange={handlePresetChange as any}
                  />
                </Form.Item>

                <Form.Item label="Save Result">
                  <Space>
                    <Switch
                      checked={saveResultContent}
                      onChange={setSaveResultContent}
                      checkedChildren="Yes"
                      unCheckedChildren="No"
                    />
                    <span className="save-hint">Saves result to existing Tracker if one exists</span>
                  </Space>
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                    loading={testingContent}
                    block
                  >
                    Test Sync
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>

          {/* Test by Tracker ID */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <SearchOutlined />
                  Test by Tracker ID
                </Space>
              }
              className="test-card"
            >
              <Form form={trackerForm} layout="vertical" onFinish={handleTestByTracker}>
                <Form.Item
                  name="tracker_id"
                  label="Tracker ID"
                  rules={[{ required: true, message: 'Please enter Tracker ID' }]}
                >
                  <InputNumber placeholder="123" style={{ width: '100%' }} min={1} />
                </Form.Item>

                <Form.Item label="Save Result">
                  <Space>
                    <Switch
                      checked={saveResultTracker}
                      onChange={setSaveResultTracker}
                      checkedChildren="Yes"
                      unCheckedChildren="No"
                    />
                    <span className="save-hint">Saves result to Tracker</span>
                  </Space>
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                    loading={testingTracker}
                    block
                  >
                    Test Sync
                  </Button>
                </Form.Item>
              </Form>

              <div className="test-info">
                <p>Tests using the settings of an existing VideoSyncTracker.</p>
              </div>
            </Card>
          </Col>

          {/* Test Result */}
          {testResult && (
            <Col xs={24}>
              <Card
                title={
                  <Space>
                    {testResult.synced ? (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    )}
                    Test Result
                    {testResult.synced ? (
                      <Tag color="green" style={{ marginLeft: 8 }}>
                        Synced
                      </Tag>
                    ) : (
                      <Tag color="red" style={{ marginLeft: 8 }}>
                        Not Synced
                      </Tag>
                    )}
                  </Space>
                }
                className={`result-card ${testResult.synced ? 'result-success' : 'result-fail'}`}
              >
                <Row gutter={[24, 16]}>
                  {/* Content Info */}
                  <Col xs={24} md={12}>
                    <div className="result-section">
                      <h4>Content Information</h4>
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Content ID">
                          <a
                            href={`/episode/edit/${testResult.content_id}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {testResult.content_id}
                          </a>
                        </Descriptions.Item>
                        <Descriptions.Item label="Content Title">
                          {testResult.content_title || '-'}
                        </Descriptions.Item>
                      </Descriptions>
                    </div>
                  </Col>

                  {/* ODK Info */}
                  <Col xs={24} md={12}>
                    <div className="result-section">
                      <h4>ODK Information</h4>
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Episode ID">
                          {testResult.episode_id || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Episode Title">
                          {testResult.episode_title || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Video Status">
                          <Tag color={testResult.video_status === 'completed' ? 'green' : 'orange'}>
                            {testResult.video_status || '-'}
                          </Tag>
                        </Descriptions.Item>
                      </Descriptions>
                    </div>
                  </Col>

                  <Col xs={24}>
                    <Divider style={{ margin: '8px 0' }} />
                  </Col>

                  {/* Preset Comparison */}
                  <Col xs={24} md={8}>
                    <div className="preset-box">
                      <h4>Expected Presets</h4>
                      <div className="preset-tags">
                        {testResult.expected_presets?.length > 0 ? (
                          testResult.expected_presets.map(preset => (
                            <Tag key={preset} color="blue">
                              {preset}
                            </Tag>
                          ))
                        ) : (
                          <span className="no-data">-</span>
                        )}
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} md={8}>
                    <div className="preset-box">
                      <h4>Found Presets</h4>
                      <div className="preset-tags">
                        {testResult.found_presets?.length > 0 ? (
                          testResult.found_presets.map(preset => (
                            <Tag key={preset} color="green" icon={<CheckCircleOutlined />}>
                              {preset}
                            </Tag>
                          ))
                        ) : (
                          <span className="no-data">-</span>
                        )}
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} md={8}>
                    <div className="preset-box">
                      <h4>Missing Presets</h4>
                      <div className="preset-tags">
                        {testResult.missing_presets?.length > 0 ? (
                          testResult.missing_presets.map(preset => (
                            <Tag key={preset} color="red" icon={<CloseCircleOutlined />}>
                              {preset}
                            </Tag>
                          ))
                        ) : (
                          <Tag color="green">None</Tag>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* URL Mismatches */}
                  {testResult.url_mismatches && testResult.url_mismatches.length > 0 && (
                    <Col xs={24}>
                      <Divider style={{ margin: '8px 0' }} />
                      <div className="url-mismatches-box">
                        <h4>
                          <WarningOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                          URL Mismatches ({testResult.url_mismatches.length})
                        </h4>
                        {testResult.url_mismatches.map((mismatch, index) => (
                          <div key={index} className="url-mismatch-item">
                            <div className="mismatch-header">
                              <Tag color="volcano">{mismatch.type}</Tag>
                              <Tag color="blue">{mismatch.preset}</Tag>
                            </div>
                            <div className="url-row">
                              <span className="url-label">Expected:</span>
                              <code className="url-value">{mismatch.expected || '-'}</code>
                            </div>
                            <div className="url-row">
                              <span className="url-label">Actual:</span>
                              <code className="url-value url-mismatch">
                                {mismatch.actual || '(missing)'}
                              </code>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Col>
                  )}

                  {/* Reason */}
                  {testResult.reason && (
                    <Col xs={24}>
                      <div className="reason-box">
                        <strong>Reason:</strong> {testResult.reason}
                      </div>
                    </Col>
                  )}

                  {/* Tracker Info & Saved Status */}
                  {(testResult.tracker_id || testResult.existing_tracker_id) && (
                    <Col xs={24}>
                      <div className="tracker-info">
                        <Space wrap>
                          <span>
                            Tracker ID:{' '}
                            <a
                              href={`/inhouse-sync-monitor/detail/${
                                testResult.tracker_id || testResult.existing_tracker_id
                              }`}
                            >
                              #{testResult.tracker_id || testResult.existing_tracker_id}
                            </a>
                          </span>
                          {testResult.tracker_status && <Tag>{testResult.tracker_status}</Tag>}
                          {testResult.tracker_check_count !== undefined && (
                            <span>Check Count: {testResult.tracker_check_count}</span>
                          )}
                          {testResult.saved && (
                            <Tag color="green" icon={<SaveOutlined />}>
                              Result Saved
                            </Tag>
                          )}
                        </Space>
                      </div>
                    </Col>
                  )}
                </Row>
              </Card>
            </Col>
          )}
        </Row>
      </div>
    </Component.BasePageComponent>
  );
};

export default observer(InhouseSyncMonitorTest);
